/** Weekly goals: the whole `weekly_goals` row for the current week in one step.
 *
 *  Picking a goal fills the weekly range by itself (`GOAL_DEFAULT_WEEKLY_RANGE`, applied in the
 *  reducer), so the common case is one dropdown and three numbers. The server propagates whatever
 *  lands here to every later week, which is why the copy says so — a coach who expected to repeat
 *  this each Sunday would otherwise do it needlessly.
 *
 *  The inputs are pre-filled from the week the server already has (see `GoalsValues.weekRow`), so
 *  clearing a field here genuinely means "clear it", which is how the endpoint behaves. */

import { useState } from "react";
import { Box, CircularProgress, FormControl, MenuItem, Select, TextField } from "@mui/material";
import { t } from "../../../dashboard/coach/coachTokens";
import { GoalTypes } from "../../../../enums/GoalTypes";
import { WeeklyRanges } from "../../../../enums/WeeklyRanges";
import StepShell, { StepGrid } from "../StepShell";
import WizardField from "../WizardField";
import { numberFieldSx, selectSx } from "../wizardStyles";
import type { GoalsValues } from "../wizardReducer";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { POINTS, energyFromInput, energyLabel, energyToInput } from "../../../nutrition/v2/ui/nutritionUnits";

interface GoalsStepProps {
  values: GoalsValues;
  error: string | null;
  /** True while the current week is being fetched — nothing can be saved before it arrives. */
  loading: boolean;
  onChange: (values: Partial<GoalsValues>) => void;
}

/**
 * The daily energy goal. `caloriesGoal` stays in kcal whatever the mode — it is what the weight
 * table stores — so a points company types points here and the field converts both ways.
 *
 * In points mode the coach's own string is kept while it still means the stored goal: converting
 * "18." to kcal and back would give "18", and the dot could never be typed.
 */
const EnergyGoalField = ({ caloriesGoal, onChange }: { caloriesGoal: string; onChange: (kcal: string) => void }) => {
  const mode = useNutritionDisplayMode();
  const [draft, setDraft] = useState("");
  const label = `יעד ${energyLabel(mode)} יומי`;
  const points = mode === POINTS;

  const toKcal = (typed: string): string =>
    typed.trim() === "" ? "" : String(energyFromInput(parseFloat(typed), mode));
  const shown = !points
    ? caloriesGoal
    : draft !== "" && toKcal(draft) === caloriesGoal
      ? draft
      : caloriesGoal === ""
        ? ""
        : String(energyToInput(Number(caloriesGoal), mode));

  return (
    <WizardField label={label} hint="היעד כאן משמש את גרף המשקל. יעדי המאקרו לתפריט נקבעים בשלב התזונה.">
      <TextField
        fullWidth
        size="small"
        type="number"
        value={shown}
        placeholder={points ? "20" : "2000"}
        inputProps={{ min: 0, step: points ? 0.5 : 50, "aria-label": label }}
        sx={numberFieldSx}
        onChange={event => {
          if (!points) return onChange(event.target.value);
          setDraft(event.target.value);
          onChange(toKcal(event.target.value));
        }}
      />
    </WizardField>
  );
};

const GoalsStep = ({ values, error, loading, onChange }: GoalsStepProps) => (
  <StepShell
    title="יעדים שבועיים"
    description="היעדים נשמרים על השבוע הנוכחי וממשיכים אוטומטית לשבועות הבאים. אפשר לשנות אותם בכל רגע מעמוד מעקב המשקל."
    error={error}
  >
    {loading ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: t.t2, fontSize: "13px" }}>
        <CircularProgress size={16} sx={{ color: t.green }} />
        טוען את השבוע הנוכחי…
      </Box>
    ) : (
      <>
        <StepGrid>
          <WizardField label="מטרה" hint="בחירת מטרה ממלאת את קצב השינוי המומלץ">
            <FormControl fullWidth size="small">
              <Select
                value={values.goal}
                displayEmpty
                inputProps={{ "aria-label": "מטרה" }}
                sx={selectSx}
                onChange={event => onChange({ goal: event.target.value as GoalsValues["goal"] })}
              >
                <MenuItem value="">לא נבחר</MenuItem>
                {GoalTypes.map(goal => (
                  <MenuItem key={goal.value} value={goal.value}>
                    {goal.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </WizardField>

          <WizardField label="קצב שינוי שבועי">
            <FormControl fullWidth size="small">
              <Select
                value={values.weeklyRange}
                displayEmpty
                inputProps={{ "aria-label": "קצב שינוי שבועי" }}
                sx={selectSx}
                onChange={event => onChange({ weeklyRange: event.target.value })}
              >
                <MenuItem value="">לא נבחר</MenuItem>
                {WeeklyRanges.map(range => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </WizardField>

          <WizardField label="יעד צעדים יומי">
            <TextField
              fullWidth
              size="small"
              type="number"
              value={values.stepsGoal}
              placeholder="10000"
              inputProps={{ min: 0, step: 500, "aria-label": "יעד צעדים יומי" }}
              sx={numberFieldSx}
              onChange={event => onChange({ stepsGoal: event.target.value })}
            />
          </WizardField>

          <WizardField label="יעד שינה (שעות)">
            <TextField
              fullWidth
              size="small"
              type="number"
              value={values.sleepGoalHours}
              placeholder="7.5"
              inputProps={{ min: 0, max: 24, step: 0.5, "aria-label": "יעד שינה בשעות" }}
              sx={numberFieldSx}
              onChange={event => onChange({ sleepGoalHours: event.target.value })}
            />
          </WizardField>

          <EnergyGoalField
            caloriesGoal={values.caloriesGoal}
            onChange={caloriesGoal => onChange({ caloriesGoal })}
          />
        </StepGrid>

        <Box sx={{ marginTop: "18px", fontSize: "11.5px", color: t.t3 }}>
          אפשר להשאיר שדות ריקים — שדה ריק נשמר כ״ללא יעד״.
        </Box>
      </>
    )}
  </StepShell>
);

export default GoalsStep;
